"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  currentDivider,
  dividerPartner,
  voltageDivider,
} from "@/lib/tool-kit/physics-waves/resistor-network";
import {
  AMP_UNITS,
  findUnit,
  OHM_UNITS,
  toBase,
  VOLT_UNITS,
} from "@/lib/tool-kit/physics-waves/units";

export interface DividerStrings {
  results: string;
  unitFor: string;
  working: string;
  enterAll: string;
  needPositive: string;
  vdGoalLabel: string;
  vdGoalOutput: string;
  vdGoalPartner: string;
  vdInput: string;
  vdTarget: string;
  vdR1: string;
  vdR2: string;
  vdOutput: string;
  vdRatio: string;
  vdCurrent: string;
  vdPowerR1: string;
  vdPowerR2: string;
  vdPartnerNeeds: string;
  vdStepOutput: string;
  vdStepCurrent: string;
  vdStepPartner: string;
  cdTotal: string;
  cdR1: string;
  cdR2: string;
  cdBranch1: string;
  cdBranch2: string;
  cdVoltage: string;
  cdShare1: string;
  cdStep1: string;
  cdStep2: string;
  cdStepVoltage: string;
}

interface Entry {
  value: string;
  unit: string;
}

function read(entry: Entry, units: typeof VOLT_UNITS): number | null {
  const parsed = parseNumberInput(entry.value);
  return parsed === null ? null : toBase(parsed, findUnit(units, entry.unit));
}

function percent(fmt: NumberFormatter, ratio: number): string {
  return `${fmt(ratio * 100, { maximumSignificantDigits: 4 })}%`;
}

type Goal = "output" | "partner";

type VdOutcome =
  | { kind: "missing" }
  | { kind: "positive" }
  | { kind: "partner" }
  | { kind: "ok"; supply: number; first: number; second: number };

/** Sort the divider's inputs into "not yet", "cannot be", or the three resistances. */
function vdOutcome(
  goal: Goal,
  supply: number | null,
  first: number | null,
  target: number | null,
  typedSecond: number | null
): VdOutcome {
  if (supply === null || first === null) {
    return { kind: "missing" };
  }
  if (goal === "output") {
    if (typedSecond === null) {
      return { kind: "missing" };
    }
    const valid = first > 0 && typedSecond > 0 && supply > 0;
    return valid
      ? { kind: "ok", supply, first, second: typedSecond }
      : { kind: "positive" };
  }
  if (target === null) {
    return { kind: "missing" };
  }
  const second = dividerPartner(supply, target, first);
  return second === null
    ? { kind: "partner" }
    : { kind: "ok", supply, first, second };
}

function VoltageDividerResults({
  outcome,
  goal,
  target,
  strings,
  fmt,
  sig,
}: {
  outcome: Extract<VdOutcome, { kind: "ok" }>;
  goal: Goal;
  target: number;
  strings: DividerStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const { supply, first, second } = outcome;
  const result = voltageDivider(supply, first, second);
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const lines = [
    t(strings.vdStepOutput, {
      vin: show(supply, "V"),
      r1: show(first, "Ω"),
      r2: show(second, "Ω"),
      vout: show(result.vout, "V"),
    }),
    t(strings.vdStepCurrent, {
      vin: show(supply, "V"),
      total: show(first + second, "Ω"),
      current: show(result.current, "A"),
    }),
  ];
  if (goal === "partner") {
    lines.unshift(
      t(strings.vdStepPartner, {
        r1: show(first, "Ω"),
        vout: show(target, "V"),
        vin: show(supply, "V"),
        r2: show(second, "Ω"),
      })
    );
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.vdOutput}
          value={show(result.vout, "V")}
        />
        {goal === "partner" ? (
          <StatCard emphasis label={strings.vdR2} value={show(second, "Ω")} />
        ) : null}
        <StatCard label={strings.vdRatio} value={percent(fmt, result.ratio)} />
        <StatCard label={strings.vdCurrent} value={show(result.current, "A")} />
        <StatCard
          label={strings.vdPowerR1}
          value={show(result.current ** 2 * first, "W")}
        />
        <StatCard
          label={strings.vdPowerR2}
          value={show(result.current ** 2 * second, "W")}
        />
      </StatGrid>
      <WorkingSteps heading={strings.working} lines={lines} />
    </>
  );
}

export function VoltageDividerForm({
  strings,
  fmt,
  sig,
}: {
  strings: DividerStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [goal, setGoal] = useState<Goal>("output");
  const [vin, setVin] = useState<Entry>({ value: "12", unit: "V" });
  const [vtarget, setVtarget] = useState<Entry>({ value: "3.3", unit: "V" });
  const [r1, setR1] = useState<Entry>({ value: "10", unit: "kΩ" });
  const [r2, setR2] = useState<Entry>({ value: "5", unit: "kΩ" });
  const target = read(vtarget, VOLT_UNITS);
  const outcome = vdOutcome(
    goal,
    read(vin, VOLT_UNITS),
    read(r1, OHM_UNITS),
    target,
    read(r2, OHM_UNITS)
  );

  let body = <Notice>{strings.enterAll}</Notice>;
  if (outcome.kind === "positive") {
    body = <ErrorNotice>{strings.needPositive}</ErrorNotice>;
  } else if (outcome.kind === "partner") {
    body = <ErrorNotice>{strings.vdPartnerNeeds}</ErrorNotice>;
  } else if (outcome.kind === "ok") {
    body = (
      <VoltageDividerResults
        fmt={fmt}
        goal={goal}
        outcome={outcome}
        sig={sig}
        strings={strings}
        target={target ?? 0}
      />
    );
  }

  return (
    <>
      <PillGroup
        label={strings.vdGoalLabel}
        name="spr-vd-goal"
        onChange={setGoal}
        options={[
          { value: "output", label: strings.vdGoalOutput },
          { value: "partner", label: strings.vdGoalPartner },
        ]}
        value={goal}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <QuantityField
          id="spr-vd-vin"
          label={strings.vdInput}
          onChange={(value) => setVin({ ...vin, value })}
          onUnitChange={(unit) => setVin({ ...vin, unit })}
          unit={vin.unit}
          unitLabel={t(strings.unitFor, { name: strings.vdInput })}
          units={VOLT_UNITS}
          value={vin.value}
        />
        <QuantityField
          id="spr-vd-r1"
          label={strings.vdR1}
          onChange={(value) => setR1({ ...r1, value })}
          onUnitChange={(unit) => setR1({ ...r1, unit })}
          unit={r1.unit}
          unitLabel={t(strings.unitFor, { name: strings.vdR1 })}
          units={OHM_UNITS}
          value={r1.value}
        />
        {goal === "output" ? (
          <QuantityField
            id="spr-vd-r2"
            label={strings.vdR2}
            onChange={(value) => setR2({ ...r2, value })}
            onUnitChange={(unit) => setR2({ ...r2, unit })}
            unit={r2.unit}
            unitLabel={t(strings.unitFor, { name: strings.vdR2 })}
            units={OHM_UNITS}
            value={r2.value}
          />
        ) : (
          <QuantityField
            id="spr-vd-target"
            label={strings.vdTarget}
            onChange={(value) => setVtarget({ ...vtarget, value })}
            onUnitChange={(unit) => setVtarget({ ...vtarget, unit })}
            unit={vtarget.unit}
            unitLabel={t(strings.unitFor, { name: strings.vdTarget })}
            units={VOLT_UNITS}
            value={vtarget.value}
          />
        )}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

export function CurrentDividerForm({
  strings,
  fmt,
  sig,
}: {
  strings: DividerStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [total, setTotal] = useState<Entry>({ value: "100", unit: "mA" });
  const [r1, setR1] = useState<Entry>({ value: "100", unit: "Ω" });
  const [r2, setR2] = useState<Entry>({ value: "300", unit: "Ω" });
  const current = read(total, AMP_UNITS);
  const first = read(r1, OHM_UNITS);
  const second = read(r2, OHM_UNITS);
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);

  let body = <Notice>{strings.enterAll}</Notice>;
  if (current !== null && first !== null && second !== null) {
    if (current < 0 || first <= 0 || second <= 0) {
      body = <ErrorNotice>{strings.needPositive}</ErrorNotice>;
    } else {
      const result = currentDivider(current, first, second);
      body = (
        <>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.cdBranch1}
              value={show(result.i1, "A")}
            />
            <StatCard
              emphasis
              label={strings.cdBranch2}
              value={show(result.i2, "A")}
            />
            <StatCard
              label={strings.cdVoltage}
              value={show(result.volts, "V")}
            />
            <StatCard
              label={strings.cdShare1}
              value={percent(fmt, second / (first + second))}
            />
          </StatGrid>
          <WorkingSteps
            heading={strings.working}
            lines={[
              t(strings.cdStep1, {
                total: show(current, "A"),
                r2: show(second, "Ω"),
                sum: show(first + second, "Ω"),
                i1: show(result.i1, "A"),
              }),
              t(strings.cdStep2, {
                total: show(current, "A"),
                i1: show(result.i1, "A"),
                i2: show(result.i2, "A"),
              }),
              t(strings.cdStepVoltage, {
                i1: show(result.i1, "A"),
                r1: show(first, "Ω"),
                volts: show(result.volts, "V"),
              }),
            ]}
          />
        </>
      );
    }
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <QuantityField
          id="spr-cd-total"
          label={strings.cdTotal}
          onChange={(value) => setTotal({ ...total, value })}
          onUnitChange={(unit) => setTotal({ ...total, unit })}
          unit={total.unit}
          unitLabel={t(strings.unitFor, { name: strings.cdTotal })}
          units={AMP_UNITS}
          value={total.value}
        />
        <QuantityField
          id="spr-cd-r1"
          label={strings.cdR1}
          onChange={(value) => setR1({ ...r1, value })}
          onUnitChange={(unit) => setR1({ ...r1, unit })}
          unit={r1.unit}
          unitLabel={t(strings.unitFor, { name: strings.cdR1 })}
          units={OHM_UNITS}
          value={r1.value}
        />
        <QuantityField
          id="spr-cd-r2"
          label={strings.cdR2}
          onChange={(value) => setR2({ ...r2, value })}
          onUnitChange={(unit) => setR2({ ...r2, unit })}
          unit={r2.unit}
          unitLabel={t(strings.unitFor, { name: strings.cdR2 })}
          units={OHM_UNITS}
          value={r2.value}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
